import type { ReactNode } from 'react';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Divider, EmptyState, TextField } from '@meshble/molo';
import { api, ApiError, must, mustOk } from './lib/api';
import { Anello, Brandmark } from './brand';
import { Building } from './icons';
import { t } from './lib/lingua';

/**
 * L'utente della sessione. È la prima chiamata dell'applicazione: da qui
 * escono le organizzazioni, quindi il selettore, quindi ogni rotta.
 *
 * Un 401 non è un errore da riprovare, è "non sei entrato": si distingue qui
 * una volta sola invece che in ogni schermata.
 */
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => must(api.GET('/api/v1/me', {})),
    retry: (count, err) => !(err instanceof ApiError && err.status === 401) && count < 2,
    staleTime: 5 * 60 * 1000,
  });
}

/** Cosa si può fare in un'organizzazione: `permesso` è un permesso del catalogo, come `server.create`. */
export type Permessi = (permesso: string) => boolean;

/**
 * Cosa può fare chi guarda in un'organizzazione, da `/me`. Serve a nascondere
 * i controlli che il ruolo non consente: è cortesia verso chi guarda,
 * l'autorizzazione vera è `Can()` lato hub. Lo staff riceve tutto il catalogo
 * da `/me`, quindi qui non serve un caso a parte per lui.
 */
export function usePermessi(slug: string): Permessi {
  const me = useMe();
  const permessi = me.data?.organizations?.find((o) => o.slug === slug)?.permissions;
  return (permesso) => !!permessi?.includes(permesso);
}

/**
 * I permessi di chi guarda su un'istanza o su un server: il ruolo più gli
 * accessi sulla risorsa (blocco 35.5). Un cliente non ha permessi
 * d'organizzazione, e senza questi vedrebbe ogni pulsante disabilitato.
 * Finché non arrivano valgono quelli dell'organizzazione.
 */
export function usePermessiSu(slug: string, risorsa: { istanza: string } | { server: string }): Permessi {
  const org = usePermessi(slug);
  const q = useQuery({
    queryKey: ['permessi', slug, risorsa],
    queryFn: () => 'istanza' in risorsa
      ? must(api.GET('/api/v1/orgs/{slug}/instances/{id}/permessi', { params: { path: { slug, id: risorsa.istanza } } }))
      : must(api.GET('/api/v1/orgs/{slug}/servers/{id}/permessi', { params: { path: { slug, id: risorsa.server } } })),
    staleTime: 60 * 1000,
  });
  const permessi = q.data?.permessi;
  return permessi ? (p) => permessi.includes(p) : org;
}

export function isUnauthenticated(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/**
 * La schermata di ingresso.
 *
 * Quali vie esistano lo dice l'hub, non questa pagina: mostrare un modulo che
 * in produzione risponde 404, o un bottone verso un IdP che non è configurato,
 * sarebbe peggio di non mostrare niente.
 */
export function Login({ sopra }: { sopra?: ReactNode } = {}) {
  const qc = useQueryClient();
  const modes = useQuery({
    queryKey: ['auth-modes'],
    queryFn: () => must(api.GET('/api/v1/auth/modes', {})),
    staleTime: Infinity,
  });

  const [email, setEmail] = useState('');
  const devLogin = useMutation({
    mutationFn: () => mustOk(api.POST('/api/v1/auth/dev-login', { body: { email } })),
    // La sessione è cambiata: quello che sapevamo dell'utente non vale più.
    onSuccess: () => qc.invalidateQueries(),
  });

  const oidc = modes.data?.oidc ?? false;
  const dev = modes.data?.dev ?? false;
  const domini = modes.data?.domains ?? [];

  return (
    <main className="md-login">
      <div className="md-login__card">
        <Brandmark />
        <p className="md-body-medium md-login__claim">{t('I tuoi server Odoo, gestiti da Meshble.')}</p>

        {/* Chi arriva da un invito deve sapere cosa sta per accettare prima di
            entrare, non dopo: `redirect` lo riporta qui e l'accettazione
            riparte da sola. */}
        {sopra}

        {modes.isSuccess && !oidc && !dev && (
          <Banner tone="error" title={t("L'accesso non è configurato")}>
            {t('Chi gestisce Molo deve collegare un fornitore di identità (OIDC) prima che si possa entrare.')}
          </Banner>
        )}

        {oidc && (
          <>
            <Button
              variant="filled"
              size="lg"
              className="md-login__cta"
              onClick={() => {
                const back = window.location.pathname + window.location.search;
                window.location.href = `/api/v1/auth/login?redirect=${encodeURIComponent(back)}`;
              }}
            >
              {t('Entra con Google Workspace')}
            </Button>
            {domini.length > 0 && (
              <p className="md-login__fine">
                {t('Solo indirizzi {domini}', { domini: domini.map((d) => `@${d}`).join(', ') })}
              </p>
            )}
          </>
        )}

        {dev && (
          <>
            {oidc && <Divider className="md-login__sep" />}
            {/* Deliberatamente sobrio: è una scorciatoia di sviluppo, non una
                seconda via d'accesso da far sembrare normale. */}
            <form
              className="md-login__dev"
              onSubmit={(e) => {
                e.preventDefault();
                devLogin.mutate();
              }}
            >
              <TextField
                label={t('Email')}
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('tu@meshble.com')}
                help={oidc ? t('Accesso di sviluppo, senza password.') : t('Ambiente di sviluppo: basta l’email, senza password.')}
              />
              {devLogin.error && (
                <Banner tone="error" title={t('Accesso non riuscito')}>{devLogin.error.message}</Banner>
              )}
              <Button
                type="submit"
                variant={oidc ? 'outlined' : 'filled'}
                className="md-login__cta"
                icon={devLogin.isPending ? <Anello /> : undefined}
                disabled={devLogin.isPending || email.trim() === ''}
              >
                {devLogin.isPending ? t('Entro…') : t('Entra')}
              </Button>
            </form>
          </>
        )}
      </div>
    </main>
  );
}

/**
 * Chi è entrato ma non è ancora in nessuna organizzazione.
 *
 * Non è un vicolo cieco: `POST /orgs` non richiede alcun permesso — è la via
 * con cui un utente nuovo comincia, ed è anche il modo in cui nasce la prima
 * organizzazione di un'installazione appena fatta.
 */
export function NoOrganizations({ email }: { email?: string }) {
  const qc = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [slug, setSlug] = useState('');

  const crea = useMutation({
    mutationFn: () => must(api.POST('/api/v1/orgs', { body: { slug, name: '' } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });

  const slugValido = /^[a-z0-9][a-z0-9-]*$/.test(slug) && slug.length >= 2;

  return (
    <EmptyState
      icon={<Building />}
      title={t('Nessuna organizzazione')}
      action={
        aperto ? undefined : (
          <div className="md-empty__actions">
            <Button variant="filled" onClick={() => setAperto(true)}>{t('Crea organizzazione')}</Button>
          </div>
        )
      }
    >
      {email
        ? t('Sei dentro come {email}, ma non fai ancora parte di nessuna organizzazione. Chiedi a chi la amministra di invitarti, oppure creane una tua.', { email: <strong>{email}</strong> })
        : t('Non fai ancora parte di nessuna organizzazione. Chiedi a chi la amministra di invitarti, oppure creane una tua.')}
      {aperto && (
        <form
          className="md-empty__form"
          onSubmit={(e) => {
            e.preventDefault();
            crea.mutate();
          }}
        >
          <TextField
            label={t('Nome breve')}
            value={slug}
            autoFocus
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            error={slug.length > 0 && !slugValido}
            help={t('Solo minuscole, cifre e trattini, es. «meshble». Compare nei link e non si cambia più.')}
          />
          {crea.error && <Banner tone="error" title={t('Non creata')}>{crea.error.message}</Banner>}
          <div className="md-empty__actions">
            <Button type="button" variant="text" onClick={() => setAperto(false)}>{t('Annulla')}</Button>
            <Button type="submit" variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={!slugValido || crea.isPending}>
              {crea.isPending ? t('Creo…') : t('Crea')}
            </Button>
          </div>
        </form>
      )}
    </EmptyState>
  );
}
